// @sympllex/api-client — framework-agnostic typed client for the Sympllex worker APIs.
// PLAN-C4.0 (C4.0-D4). No React: TanStack Query hooks (C4.0-task-2) wrap these methods.
// Auth: dev injects the X-Sympllex-Dev-Session header the workers' resolveSession parses
// in ENVIRONMENT=dev; "bearer" mode attaches a WorkOS access token (C1.3 follow-up).
import type { SympllexSession } from "@sympllex/auth";
import type {
  CreateVerwerkingInput, CreateActivityInput, CreateBedrijfsmiddelInput, CreateBedreigingInput,
  CreateBestandssysteemInput,
  CreateBewijscontainerInput, CreateTicketInput, CreateProgramInput, CreatePdcaInput,
  CreateVerbeterpuntInput, CreatePartnerRoleInput, CreateProcessorAgreementInput,
  CreateProcessingScheduleInput, CreateSubProcessorInput,
  CreateSharedEngagementInput, CreateSharedDpaInput, CreateProcessorInvitationInput,
  CreateRisicoInput, CreateDpiaInput, CreateNotificationInput, CreateDsarInput,
  PolicyDocumentKind, CreateVestigingInput, AddPrivacyteamMemberInput, CreateTaskInput,
} from "@sympllex/domain";

// Re-export the domain types the framework-agnostic UI consumes through this client (type-only — the UI
// must not pull in the domain package's DB runtime). Keeps tenant-pages dependent on api-client alone.
export type { CreateVerwerkingInput, VerwerkingLineage, ActivityType, PolicyDocumentKind } from "@sympllex/domain";
export type {
  AccountabilityOverview, AccountabilitySectionRollup, MissingEvidenceRow, HoofddoelOption,
} from "@sympllex/domain";
import type { AccountabilityOverview, HoofddoelOption } from "@sympllex/domain";

/** GET /accountability/overview filters (PLAN-W27 C) — all optional; from/to are "YYYY-MM". */
export interface AccountabilityQuery {
  from?: string | undefined; to?: string | undefined;
  hoofddoelId?: string | undefined; activityId?: string | undefined;
}

export interface ApiClientConfig {
  baseUrls: { tenant: string; admin?: string; mcp?: string; billing?: string };
  /** Returns the active session (dev header injection) or null. */
  getSession?: () => SympllexSession | null | undefined;
  /** Returns a bearer access token for prod auth. */
  getToken?: () => string | null | undefined;
  /** "cookie" relies on the httpOnly sympllex_session cookie (credentialed CORS); "dev" injects the
   *  dev-session header; "bearer" attaches Authorization. Default "dev". */
  mode?: "dev" | "bearer" | "cookie";
  /** Override fetch (tests / non-global environments). Defaults to globalThis.fetch. */
  fetchImpl?: typeof fetch;
  /** Called on a 401 (cookie mode): attempt a silent session refresh. Returns true if it succeeded, in
   *  which case the original request is retried once. The 15-min access-token cookie rotates this way
   *  off the 14-day session — without it every call 401s ~15 min after login. The hook should de-dupe
   *  concurrent refreshes (single in-flight promise). */
  onUnauthorized?: () => Promise<boolean>;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) { super(message); this.name = "ApiError"; }
}

function authHeaders(cfg: ApiClientConfig): Record<string, string> {
  const mode = cfg.mode ?? "dev";
  if (mode === "cookie") return {}; // the httpOnly session cookie travels via credentials:include
  if (mode === "bearer") {
    const tok = cfg.getToken?.();
    return tok ? { authorization: `Bearer ${tok}` } : {};
  }
  const s = cfg.getSession?.();
  return s ? { "x-sympllex-dev-session": JSON.stringify(s) } : {};
}

/** The single network primitive: send the request credentialed, and on a 401 (expired 15-min
 *  access-token cookie) silently refresh once off the 14-day session and retry. EVERY call — JSON,
 *  file upload/download, document export — goes through here, so expiry recovery is uniform and no
 *  endpoint hard-fails on an expired token. `onUnauthorized` must de-dupe concurrent refreshes
 *  (single in-flight promise) so a burst of 401s triggers exactly one /auth/refresh. */
async function sendWithRetry(cfg: ApiClientConfig, url: string, init?: RequestInit): Promise<Response> {
  const f = cfg.fetchImpl ?? globalThis.fetch;
  if (!f) throw new Error("no fetch implementation available");
  const send = () => f(url, {
    ...init,
    credentials: "include", // send the sympllex_session cookie (CORS Allow-Credentials)
    headers: { ...authHeaders(cfg), ...(init?.headers ?? {}) },
  });
  let res = await send();
  if (res.status === 401 && cfg.onUnauthorized && (await cfg.onUnauthorized())) res = await send();
  return res;
}

async function request<T>(cfg: ApiClientConfig, base: string, path: string, init?: RequestInit): Promise<T> {
  const res = await sendWithRetry(cfg, `${base}${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, body, `${init?.method ?? "GET"} ${path} → ${res.status}`);
  return body as T;
}
function safeJson(t: string): unknown { try { return JSON.parse(t); } catch { return t; } }

// Tenant-worker resource paths (must match apps/tenant-worker route map).
export const TENANT_PATHS = {
  verwerkingen: "/verwerkingen", activities: "/activities", bedrijfsmiddelen: "/bedrijfsmiddelen",
  bedreigingen: "/bedreigingen", bestandssystemen: "/bestandssystemen", vestigingen: "/vestigingen", tasks: "/tasks",
  bewijscontainers: "/bewijscontainers", tickets: "/tickets", risico: "/risico", dpia: "/dpia", notifications: "/notifications", dsar: "/dsar",
  implementationPrograms: "/implementation-programs", pdcaSchedules: "/pdca-schedules",
  verbeterpunten: "/verbeterpunten", partnerRoles: "/partner-roles", externalParties: "/external-parties",
  processorAgreements: "/processor-agreements", processingSchedules: "/processing-schedules",
  subProcessors: "/sub-processors",
  sharedEngagements: "/shared/engagements", sharedEngagementConfirm: "/shared/engagement-confirm",
  sharedDpas: "/shared/dpas", sharedInvitations: "/shared/invitations",
  auditorEngagements: "/auditor-engagements",
  sprintGateDecisions: "/sprint-gate-decisions", changeTriggers: "/change-triggers",
  transfers: "/transfers", actielijst: "/actielijst",
} as const;

export interface SympllexApiClient {
  // tenant — detail / edit
  getVerwerking(id: string): Promise<unknown>;
  updateVerwerking(id: string, patch: Record<string, unknown>): Promise<unknown>;
  // generic detail/edit/deactivate for any tenant entity (PLAN-P3 U1). `base` = a TENANT_PATHS value.
  getEntity(base: string, id: string): Promise<unknown>;
  updateEntity(base: string, id: string, patch: Record<string, unknown>): Promise<unknown>;
  deactivateEntity(base: string, id: string): Promise<unknown>;
  // RoPA Art.30 child junctions (PLAN-P3 U2a): kind = doelen | recipients | retention | legal-basis |
  // personal-data-categories | data-subject-categories | bestandssystemen. `doelId` scopes the per-purpose
  // junctions to one Doel (Phase A); omit for the verwerking-wide list.
  listVerwerkingChild(verwerkingId: string, kind: string, doelId?: string): Promise<unknown[]>;
  addVerwerkingChild(verwerkingId: string, kind: string, input: Record<string, unknown>): Promise<unknown>;
  removeVerwerkingChild(verwerkingId: string, kind: string, childId: string): Promise<unknown>;
  // Doel (purpose) get/update — the per-purpose Art.30 home (Phase A).
  getVerwerkingDoel(verwerkingId: string, doelId: string): Promise<unknown>;
  updateVerwerkingDoel(verwerkingId: string, doelId: string, patch: Record<string, unknown>): Promise<unknown>;
  // Per-doel BIV/Risico (Phase A refinement).
  getDoelBiv(verwerkingId: string, doelId: string): Promise<unknown>;
  upsertDoelBiv(verwerkingId: string, doelId: string, patch: Record<string, unknown>): Promise<unknown>;
  // RoPA canonical picker libraries (PLAN-P3 U2b)
  listLegalBases(): Promise<unknown[]>;
  listPersonalDataCategories(): Promise<unknown[]>;
  listDataSubjectCategories(): Promise<unknown[]>;
  // Canonical ticket-kind library (round-3 tickets depth, PLAN-W27 D). Counsel-gated content —
  // ships EMPTY until seeded; the UI degrades honestly on an empty result.
  listTicketKinds(): Promise<unknown[]>;
  // Risk register (PLAN-P3 U3)
  listRisico(): Promise<unknown[]>;
  createRisico(i: CreateRisicoInput): Promise<unknown>;
  // Verwerking change history (PLAN-P3 U4)
  listVerwerkingHistory(verwerkingId: string): Promise<unknown[]>;
  // Verwerking ↔ canonical-Activity lineage (FRONTEND-BLUEPRINT §2.3) — Verordening › Section › Activity.
  getVerwerkingLineage(verwerkingId: string): Promise<unknown>;
  // In-app notifications (PLAN-P5 U4)
  listNotifications(): Promise<unknown[]>;
  createNotification(i: CreateNotificationInput): Promise<unknown>;
  markNotificationRead(id: string): Promise<unknown>;
  // Tenant settings (PLAN-P1/P2 onboarding)
  getTenantSettings(): Promise<unknown>;
  updateTenantSettings(patch: { name?: string; defaultLanguage?: string }): Promise<unknown>;
  // Organisatiegegevens (PLAN-W29) — the tenant's own Organisation master data; tenant edits trade name
  // + contact. Gated tenant_settings.read / .update.
  getOrganisationDetails(): Promise<unknown>;
  updateOrganisationDetails(patch: { tradeName?: string | null; contact?: Record<string, string | null> }): Promise<unknown>;
  getJurisdictionLanguageSettings(): Promise<unknown>;
  updateJurisdictionLanguageSettings(patch: { additionalJurisdictionIds?: string[]; jurisdictionLanguages?: Record<string, string> }): Promise<unknown>;
  // Organisatie · Vestigingen (PLAN-W29) — establishments register. Detail/edit/deactivate via the
  // generic getEntity/updateEntity/deactivateEntity over TENANT_PATHS.vestigingen.
  listVestigingen(): Promise<unknown[]>;
  createVestiging(i: CreateVestigingInput): Promise<unknown>;
  // Organisatie · Privacyteam (PLAN-W29) — roster (Org × User × Functie) + pickers.
  listPrivacyteam(): Promise<unknown[]>;
  addPrivacyteamMember(i: AddPrivacyteamMemberInput): Promise<unknown>;
  removePrivacyteamMember(id: string): Promise<unknown>;
  listPrivacyteamFuncties(): Promise<unknown[]>;
  listPrivacyteamUsers(): Promise<unknown[]>;
  // Planner · Taken (PLAN-W30) — tasks. Detail/edit/deactivate via the generic getEntity/updateEntity/
  // deactivateEntity over TENANT_PATHS.tasks. Pickers: enabled activities + tenant users.
  listTasks(): Promise<unknown[]>;
  createTask(i: CreateTaskInput): Promise<unknown>;
  listTaskActivities(): Promise<unknown[]>;
  listTaskAssignees(): Promise<unknown[]>;
  // Organisatie · Ticket instellen (PLAN-W30) — per ticket-kind procedure-template config.
  listTicketSettings(): Promise<unknown[]>;
  upsertTicketSettings(i: { ticketKindId: string; procedureTemplateBody: string }): Promise<unknown>;
  // DSAR register (PLAN-P3 X7)
  listDsar(): Promise<unknown[]>;
  createDsar(i: CreateDsarInput): Promise<unknown>;
  // RoPA Art.30 export (PLAN-P3 X6) — returns the raw CSV/XML document text.
  exportRopa(format: "csv" | "xml" | "privacy-statement"): Promise<string>;
  // Evidence files (PLAN-P12 D4/X2) — real bytes in R2 via the worker proxy.
  listEvidenceFiles(bewijscontainerId: string): Promise<unknown[]>;
  uploadEvidenceFile(bewijscontainerId: string, file: File, evidenceKind: string): Promise<unknown>;
  downloadEvidenceFile(bewijscontainerId: string, fileId: string): Promise<Blob>;
  deleteEvidenceFile(bewijscontainerId: string, fileId: string): Promise<unknown>;
  // Art.46 transfer register (D13)
  listTransfers(): Promise<unknown[]>;
  createTransfer(i: Record<string, unknown>): Promise<unknown>;
  listTransferMechanisms(): Promise<unknown[]>;
  // Methodology D3b — sprint-gate decision log + ChangeTrigger feed.
  listSprintGateDecisions(): Promise<unknown[]>;
  createSprintGateDecision(i: { implementationProgramId: string; sprintNumber: string; sprintStageCode: string; decision: string; rationale: string }): Promise<unknown>;
  listChangeTriggers(): Promise<unknown[]>;
  // Auditor engagements (D12) — the audited tenant's view.
  listAuditorEngagements(): Promise<unknown[]>;
  // Generic entity audit trail / change history (X3).
  listEntityAudit(base: string, id: string): Promise<unknown[]>;
  // Server-side search + pagination (X4b) — { rows, total, limit, offset }.
  listPaged(base: string, params: { q?: string; limit?: number; offset?: number }): Promise<{ rows: unknown[]; total: number; limit: number; offset: number }>;
  // Upcoming/overdue compliance deadlines for the cockpit (PLAN-P6).
  listDeadlines(): Promise<unknown[]>;
  // Centrale Actielijst — prioritised cross-aggregate action list (PLAN-P15).
  listActielijst(): Promise<unknown[]>;
  // AI-assisted DPIA draft (D7) — returns CNIL 4-step draft markdown for the human to review + save.
  draftDpiaWithAi(verwerkingId: string): Promise<{ draftMarkdown?: string; error?: string }>;
  // DPIA document export (PLAN-P3 X6) — returns the print-friendly HTML document.
  exportDpia(id: string): Promise<string>;
  // DSAR packet export (PLAN-P14 X7) — returns the print-friendly HTML response packet.
  exportDsarPacket(id: string): Promise<string>;
  // Trust Exchange public page (D10) — tenant-managed singleton.
  getMyTrustPage(): Promise<unknown>;
  updateMyTrustPage(patch: { slug: string; isPublic?: boolean; displaySummaryMarkdown?: string | null; showCertifications?: boolean; allowDirectoryDiscovery?: boolean }): Promise<unknown>;
  // DPIA authoring (PLAN-P3 U5)
  listDpia(): Promise<unknown[]>;
  createDpia(i: CreateDpiaInput): Promise<unknown>;
  // DPIA sign-off lifecycle (PLAN-P10 D2)
  transitionDpia(id: string, action: "submit_review" | "approve" | "request_changes" | "reject" | "reopen"): Promise<unknown>;
  // Planner — Regime configureren (FRONTEND-BLUEPRINT §2.4/§3.3): the per-tenant compliance-regime
  // activation surface (org_activity_config overlay). Canonical activities + the tenant's regime overlay
  // + the Inschakelen toggle. RLS-scoped; gated verwerking.read / verwerking.write.
  listCanonicalActivities(regulationId?: string): Promise<unknown[]>;
  getRegime(): Promise<unknown[]>;
  setActivityEnabled(activityId: string, enabled: boolean): Promise<unknown>;
  /** A single Activity's detail (meta + overlay + linked Bewijsmiddelen with evidence status). */
  getActivityDetail(activityId: string): Promise<unknown>;
  /** Risk-triad drill-in detail (entity + linked rows): asset/threat/risico. */
  getAssetDetail(id: string): Promise<unknown>;
  getThreatDetail(id: string): Promise<unknown>;
  getRisicoDetail(id: string): Promise<unknown>;
  /** Inspecteur drill-in detail composites (Batch 2). */
  getBestandssysteemDetail(id: string): Promise<unknown>;
  getBewijscontainerDetail(id: string): Promise<unknown>;
  getRegulationDetail(id: string): Promise<unknown>;
  /** Methodology drill-in detail composites (Batch 3). */
  getProgramDetail(id: string): Promise<unknown>;
  getVerbeterpuntDetail(id: string): Promise<unknown>;
  getSprintGateDetail(id: string): Promise<unknown>;
  getChangeTriggerDetail(id: string): Promise<unknown>;
  /** Partner-role drill-in detail (Batch 4): the Art.28 DPA chain + verwerking bindings. */
  getPartnerRoleDetail(id: string): Promise<unknown>;
  /** Archief (Theme 2): list the tenant's soft-deleted records + restore one. */
  listArchive(): Promise<unknown[]>;
  restoreEntity(base: string, id: string): Promise<unknown>;
  // L11 — apply the Gouden Standaard onboarding starter (11 baseline processings; idempotent).
  applyStarterTemplate(): Promise<{ applied: boolean; created: number; reason?: string }>;
  // L7 — Mijn tickets: the tickets assigned to the signed-in user.
  listMyTickets(): Promise<unknown[]>;
  // Beleidsmaker persona singletons (PLAN-W27 A; FRONTEND-BLUEPRINT §3.2): one policy document per
  // (tenant, kind[, regulation]) — kind ∈ mission | conduct | goal_planning. GET returns null when the
  // singleton was never saved; PUT upserts it. Templates = the admin policy-templates canonical library
  // (global reference data; may be empty). Gated policy_document.read / policy_document.update.
  getPolicyDocument(kind: PolicyDocumentKind, regulationId?: string): Promise<unknown>;
  savePolicyDocument(kind: PolicyDocumentKind, input: { body: string; regulationId?: string | null; appliedTemplateId?: string | null }): Promise<unknown>;
  listPolicyTemplates(): Promise<unknown[]>;
  // Inspecteur — Wet- en regelgeving (PLAN-W27 B): canonical regulations + jurisdiction + the
  // tenant's enabled state, and the Toevoegen/Verwijderen toggle (tenant_regulation_enablement).
  listRegulations(): Promise<unknown[]>;
  setRegulationEnabled(regulationId: string, enabled: boolean): Promise<unknown>;
  // Controller — Verantwoording (PLAN-W27 C): the COMPUTED accountability overview (enabled regime ×
  // linked bewijscontainers × file validity at the as-of date) + the Hoofddoel filter options.
  // GET /accountability/overview; gated bewijscontainer.read.
  getAccountabilityOverview(filters?: AccountabilityQuery): Promise<AccountabilityOverview & { hoofddoelen: HoofddoelOption[] }>;
  // tenant — read
  listVerwerkingen(): Promise<unknown[]>;
  listActivities(): Promise<unknown[]>;
  listBedrijfsmiddelen(): Promise<unknown[]>;
  listBedreigingen(): Promise<unknown[]>;
  listBestandssystemen(): Promise<unknown[]>;
  listBewijscontainers(): Promise<unknown[]>;
  listTickets(): Promise<unknown[]>;
  listImplementationPrograms(): Promise<unknown[]>;
  listPdcaSchedules(): Promise<unknown[]>;
  listVerbeterpunten(): Promise<unknown[]>;
  listPartnerRoles(): Promise<unknown[]>;
  // D5 (W34) — tenant external-party directory (the source partner_role roots on).
  listExternalParties(): Promise<unknown[]>;
  createExternalParty(i: { name: string; legalName?: string; partyKind?: string; country?: string; contactName?: string; contactEmail?: string; notes?: string }): Promise<unknown>;
  listProcessorAgreements(): Promise<unknown[]>;
  listProcessingSchedules(): Promise<unknown[]>;
  listSubProcessors(): Promise<unknown[]>;
  // tenant — create
  createVerwerking(i: CreateVerwerkingInput): Promise<unknown>;
  createActivity(i: CreateActivityInput): Promise<unknown>;
  createBedrijfsmiddel(i: CreateBedrijfsmiddelInput): Promise<unknown>;
  createBedreiging(i: CreateBedreigingInput): Promise<unknown>;
  createBestandssysteem(i: CreateBestandssysteemInput): Promise<unknown>;
  createBewijscontainer(i: CreateBewijscontainerInput): Promise<unknown>;
  createTicket(i: CreateTicketInput): Promise<unknown>;
  createImplementationProgram(i: CreateProgramInput): Promise<unknown>;
  createPdcaSchedule(i: CreatePdcaInput): Promise<unknown>;
  createVerbeterpunt(i: CreateVerbeterpuntInput): Promise<unknown>;
  createPartnerRole(i: CreatePartnerRoleInput): Promise<unknown>;
  createProcessorAgreement(i: CreateProcessorAgreementInput): Promise<unknown>;
  createProcessingSchedule(i: CreateProcessingScheduleInput): Promise<unknown>;
  createSubProcessor(i: CreateSubProcessorInput): Promise<unknown>;
  // cross-tenant shared aggregates (C6.0)
  listSharedEngagements(): Promise<unknown[]>;
  createSharedEngagement(i: CreateSharedEngagementInput): Promise<unknown>;
  confirmSharedEngagement(engagementId: string): Promise<unknown>;
  listSharedDpas(): Promise<unknown[]>;
  createSharedDpa(i: CreateSharedDpaInput): Promise<unknown>;
  listProcessorInvitations(): Promise<unknown[]>;
  createProcessorInvitation(i: CreateProcessorInvitationInput): Promise<unknown>;
  // admin canonical libraries (admin-worker; routes are under /admin/<library>)
  listCatalog(library: string): Promise<unknown[]>;
  getCatalogRow(library: string, id: string): Promise<unknown>;
  createCatalogRow(library: string, body: Record<string, unknown>): Promise<unknown>;
  updateCatalogRow(library: string, id: string, patch: Record<string, unknown>): Promise<unknown>;
  softDeleteCatalogRow(library: string, id: string): Promise<unknown>;
  restoreCatalogRow(library: string, id: string): Promise<unknown>;
  // consultancy-firm detail + certifications (admin-worker)
  /** Firm detail: the firm row + its professional certifications. */
  getConsultancyFirmDetail(firmId: string): Promise<Record<string, unknown>>;
  addFirmCertification(firmId: string, input: Record<string, unknown>): Promise<unknown>;
  deleteFirmCertification(firmId: string, certificationId: string): Promise<unknown>;
  // super-tenant billing overview (admin-worker)
  listSubscriptions(): Promise<{ subscriptions: Array<Record<string, unknown>>; totals: Record<string, number | string> }>;
  // counsel-review queue (admin-worker)
  listCounselReviews(): Promise<Array<Record<string, unknown>>>;
  /** Full verdict detail: review fields + linked AI prefill run + the section draft under review. */
  getCounselReviewDetail(id: string): Promise<Record<string, unknown>>;
  submitCounselVerdict(id: string, verdict: "approved" | "changes_requested" | "rejected", notes?: string): Promise<unknown>;
  // billing (stripe-webhook-worker /checkout-session) — per-action (PLAN-C4.4); quantity
  // = the tenant's active-action count, computed server-side.
  createCheckoutSession(input: { tenantId: string; returnUrl: string; customerId?: string }): Promise<{ clientSecret: string; sessionId: string; actionCount?: number; quantity?: number }>;
  // billing self-serve (D8) — auth-gated on the tenant-worker; the tenant is the session's, not a param.
  createPortalSession(returnUrl?: string): Promise<{ url?: string; error?: string }>;
  listInvoices(): Promise<{ invoices: Array<Record<string, unknown>> }>;
  // C6.1 Trust Exchange
  listVisibleMeasures(): Promise<unknown>;
  publishMeasure(input: unknown): Promise<unknown>;
  consumeMeasure(input: unknown): Promise<unknown>;
  listConsumedMeasures(): Promise<unknown>;
  listMeasureMappings(measureId: string): Promise<unknown>;
  mapMeasureToArticle(input: unknown): Promise<unknown>;
  listMeasuresByArticle(regulationArticleId: string): Promise<unknown>;
  attachMeasureEvidence(input: unknown): Promise<unknown>;
  listMeasureEvidence(measureId: string): Promise<unknown>;
  // C4.5 compliance rollup + C3.1 eval runs (admin)
  getComplianceRollup(tenantId: string): Promise<unknown>;
  refreshComplianceRollup(): Promise<unknown>;
  listEvalRuns(evalSetId: string): Promise<unknown>;
  // C6.2 Consultant marketplace
  listApprovedConsultants(): Promise<unknown>;
  listConsultantEngagements(): Promise<unknown>;
  createConsultantEngagement(input: unknown): Promise<unknown>;
  signConsultantEngagement(engagementId: string, side: "tenant" | "consultant"): Promise<unknown>;
  // mcp JSON-RPC passthrough
  mcp(method: string, params?: unknown): Promise<unknown>;
}

export function createApiClient(cfg: ApiClientConfig): SympllexApiClient {
  const t = cfg.baseUrls.tenant;
  const admin = () => { if (!cfg.baseUrls.admin) throw new Error("admin baseUrl not configured"); return cfg.baseUrls.admin; };
  const mcpBase = () => { if (!cfg.baseUrls.mcp) throw new Error("mcp baseUrl not configured"); return cfg.baseUrls.mcp; };
  const get = <T>(path: string) => request<T>(cfg, t, path);
  const post = <T>(path: string, body: unknown) => request<T>(cfg, t, path, { method: "POST", body: JSON.stringify(body) });

  return {
    getVerwerking: (id) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${id}`),
    updateVerwerking: (id, patch) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    getEntity: (base, id) => request(cfg, t, `${base}/${id}`),
    updateEntity: (base, id, patch) => request(cfg, t, `${base}/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    deactivateEntity: (base, id) => request(cfg, t, `${base}/${id}`, { method: "DELETE" }),
    listVerwerkingChild: (vId, kind, doelId) => get(`${TENANT_PATHS.verwerkingen}/${vId}/${kind}${doelId ? `?doelId=${encodeURIComponent(doelId)}` : ""}`),
    addVerwerkingChild: (vId, kind, input) => post(`${TENANT_PATHS.verwerkingen}/${vId}/${kind}`, input),
    removeVerwerkingChild: (vId, kind, childId) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${vId}/${kind}/${childId}`, { method: "DELETE" }),
    getVerwerkingDoel: (vId, doelId) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${vId}/doelen/${doelId}`),
    updateVerwerkingDoel: (vId, doelId, patch) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${vId}/doelen/${doelId}`, { method: "PATCH", body: JSON.stringify(patch) }),
    getDoelBiv: (vId, doelId) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${vId}/doelen/${doelId}/biv`),
    upsertDoelBiv: (vId, doelId, patch) => request(cfg, t, `${TENANT_PATHS.verwerkingen}/${vId}/doelen/${doelId}/biv`, { method: "PATCH", body: JSON.stringify(patch) }),
    listLegalBases: () => get("/libraries/legal-bases"),
    listPersonalDataCategories: () => get("/libraries/personal-data-categories"),
    listDataSubjectCategories: () => get("/libraries/data-subject-categories"),
    listTicketKinds: () => get("/libraries/ticket-kinds"),
    listRisico: () => get(TENANT_PATHS.risico),
    createRisico: (i) => post(TENANT_PATHS.risico, i),
    listVerwerkingHistory: (vId) => get(`${TENANT_PATHS.verwerkingen}/${vId}/history`),
    getVerwerkingLineage: (vId) => get(`${TENANT_PATHS.verwerkingen}/${vId}/lineage`),
    listNotifications: () => get(TENANT_PATHS.notifications),
    createNotification: (i) => post(TENANT_PATHS.notifications, i),
    markNotificationRead: (id) => request(cfg, t, `${TENANT_PATHS.notifications}/${id}`, { method: "PATCH", body: JSON.stringify({ read: true }) }),
    getTenantSettings: () => request(cfg, t, "/tenant-settings"),
    updateTenantSettings: (patch) => request(cfg, t, "/tenant-settings", { method: "PATCH", body: JSON.stringify(patch) }),
    getOrganisationDetails: () => request(cfg, t, "/organisation-details"),
    updateOrganisationDetails: (patch) => request(cfg, t, "/organisation-details", { method: "PATCH", body: JSON.stringify(patch) }),
    getJurisdictionLanguageSettings: () => request(cfg, t, "/jurisdiction-language"),
    updateJurisdictionLanguageSettings: (patch) => request(cfg, t, "/jurisdiction-language", { method: "PATCH", body: JSON.stringify(patch) }),
    listVestigingen: () => get(TENANT_PATHS.vestigingen),
    createVestiging: (i) => post(TENANT_PATHS.vestigingen, i),
    listPrivacyteam: () => get("/privacyteam"),
    addPrivacyteamMember: (i) => post("/privacyteam", i),
    removePrivacyteamMember: (id) => request(cfg, t, `/privacyteam/${id}`, { method: "DELETE" }),
    listPrivacyteamFuncties: () => get("/privacyteam/functies"),
    listPrivacyteamUsers: () => get("/privacyteam/users"),
    listTasks: () => get(TENANT_PATHS.tasks),
    createTask: (i) => post(TENANT_PATHS.tasks, i),
    listTaskActivities: () => get("/tasks/activities"),
    listTaskAssignees: () => get("/tasks/assignees"),
    listTicketSettings: () => get("/ticket-settings"),
    upsertTicketSettings: (i) => request(cfg, t, "/ticket-settings", { method: "PATCH", body: JSON.stringify(i) }),
    listDsar: () => get(TENANT_PATHS.dsar),
    createDsar: (i) => post(TENANT_PATHS.dsar, i),
    listTransfers: () => get(TENANT_PATHS.transfers),
    createTransfer: (i) => post(TENANT_PATHS.transfers, i),
    listTransferMechanisms: () => get("/libraries/transfer-mechanisms"),
    listSprintGateDecisions: () => get(TENANT_PATHS.sprintGateDecisions),
    createSprintGateDecision: (i) => post(TENANT_PATHS.sprintGateDecisions, i),
    listChangeTriggers: () => get(TENANT_PATHS.changeTriggers),
    listEvidenceFiles: (bcId) => get(`${TENANT_PATHS.bewijscontainers}/${bcId}/files`),
    uploadEvidenceFile: async (bcId, file, evidenceKind) => {
      const res = await sendWithRetry(cfg, `${t}${TENANT_PATHS.bewijscontainers}/${bcId}/files`, {
        method: "POST", body: file,
        headers: { "content-type": file.type || "application/octet-stream", "x-filename": encodeURIComponent(file.name), "x-evidence-kind": evidenceKind },
      });
      if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null), `POST evidence → ${res.status}`);
      return res.json();
    },
    downloadEvidenceFile: async (bcId, fileId) => {
      const res = await sendWithRetry(cfg, `${t}${TENANT_PATHS.bewijscontainers}/${bcId}/files/${fileId}`);
      if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null), `GET evidence → ${res.status}`);
      return res.blob();
    },
    deleteEvidenceFile: (bcId, fileId) => request(cfg, t, `${TENANT_PATHS.bewijscontainers}/${bcId}/files/${fileId}`, { method: "DELETE" }),
    listAuditorEngagements: () => get(TENANT_PATHS.auditorEngagements),
    listEntityAudit: (base, id) => get(`${base}/${id}/audit`),
    listDeadlines: () => get("/deadlines"),
    listActielijst: () => get("/actielijst"),
    draftDpiaWithAi: (verwerkingId: string) => post("/dpia/ai-draft", { verwerkingId }) as Promise<{ draftMarkdown?: string; error?: string }>,
    createPortalSession: (returnUrl) => post("/billing/portal", { ...(returnUrl ? { returnUrl } : {}) }),
    listInvoices: () => get("/billing/invoices"),
    listPaged: (base, params) => {
      const qs = new URLSearchParams({ paged: "1" });
      if (params.q) qs.set("q", params.q);
      if (params.limit != null) qs.set("limit", String(params.limit));
      if (params.offset != null) qs.set("offset", String(params.offset));
      return get(`${base}?${qs.toString()}`);
    },
    getMyTrustPage: () => request(cfg, t, "/my-trust-page"),
    updateMyTrustPage: (patch) => request(cfg, t, "/my-trust-page", { method: "PATCH", body: JSON.stringify(patch) }),
    exportRopa: async (format) => {
      const res = await sendWithRetry(cfg, `${t}${TENANT_PATHS.verwerkingen}/export?format=${format}`);
      if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null), `GET /verwerkingen/export → ${res.status}`);
      return res.text();
    },
    exportDpia: async (id) => {
      const res = await sendWithRetry(cfg, `${t}${TENANT_PATHS.dpia}/${id}/export`);
      if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null), `GET /dpia/${id}/export → ${res.status}`);
      return res.text();
    },
    exportDsarPacket: async (id) => {
      const res = await sendWithRetry(cfg, `${t}${TENANT_PATHS.dsar}/${id}/export`);
      if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => null), `GET /dsar/${id}/export → ${res.status}`);
      return res.text();
    },
    listDpia: () => get(TENANT_PATHS.dpia),
    createDpia: (i) => post(TENANT_PATHS.dpia, i),
    transitionDpia: (id, action) => post(`${TENANT_PATHS.dpia}/${id}/transition`, { action }),
    listCanonicalActivities: (regulationId) => get(regulationId ? `/regime/activities?regulationId=${encodeURIComponent(regulationId)}` : "/regime/activities"),
    getRegime: () => get("/regime"),
    setActivityEnabled: (activityId, enabled) => post("/regime", { activityId, enabled }),
    getActivityDetail: (activityId) => get(`/regime/activities/${encodeURIComponent(activityId)}`),
    getAssetDetail: (id) => get(`${TENANT_PATHS.bedrijfsmiddelen}/${encodeURIComponent(id)}/detail`),
    getThreatDetail: (id) => get(`${TENANT_PATHS.bedreigingen}/${encodeURIComponent(id)}/detail`),
    getRisicoDetail: (id) => get(`${TENANT_PATHS.risico}/${encodeURIComponent(id)}/detail`),
    getBestandssysteemDetail: (id) => get(`${TENANT_PATHS.bestandssystemen}/${encodeURIComponent(id)}/detail`),
    getBewijscontainerDetail: (id) => get(`${TENANT_PATHS.bewijscontainers}/${encodeURIComponent(id)}/detail`),
    getRegulationDetail: (id) => get(`/regulations/${encodeURIComponent(id)}/detail`),
    getProgramDetail: (id) => get(`${TENANT_PATHS.implementationPrograms}/${encodeURIComponent(id)}/detail`),
    getVerbeterpuntDetail: (id) => get(`${TENANT_PATHS.verbeterpunten}/${encodeURIComponent(id)}/detail`),
    getSprintGateDetail: (id) => get(`/sprint-gate-decisions/${encodeURIComponent(id)}/detail`),
    getChangeTriggerDetail: (id) => get(`/change-triggers/${encodeURIComponent(id)}/detail`),
    getPartnerRoleDetail: (id) => get(`/partner-roles/${encodeURIComponent(id)}/detail`),
    listArchive: () => get("/archive"),
    restoreEntity: (base, id) => post(`${base}/${encodeURIComponent(id)}/restore`, {}),
    applyStarterTemplate: () => post("/onboarding/apply-starter", {}) as Promise<{ applied: boolean; created: number; reason?: string }>,
    listMyTickets: () => get("/me/tickets"),
    getPolicyDocument: (kind, regulationId) => get(regulationId ? `/policy-documents/${kind}?regulationId=${encodeURIComponent(regulationId)}` : `/policy-documents/${kind}`),
    savePolicyDocument: (kind, input) => request(cfg, t, `/policy-documents/${kind}`, { method: "PUT", body: JSON.stringify(input) }),
    listPolicyTemplates: () => get("/policy-templates"),
    listRegulations: () => get("/regulations"),
    setRegulationEnabled: (regulationId, enabled) => post("/regulations", { regulationId, enabled }),
    getAccountabilityOverview: (filters) => {
      const qs = new URLSearchParams();
      for (const k of ["from", "to", "hoofddoelId", "activityId"] as const) {
        const v = filters?.[k];
        if (v) qs.set(k, v);
      }
      const q = qs.toString();
      return get(`/accountability/overview${q ? `?${q}` : ""}`);
    },
    listVerwerkingen: () => get(TENANT_PATHS.verwerkingen),
    listActivities: () => get(TENANT_PATHS.activities),
    listBedrijfsmiddelen: () => get(TENANT_PATHS.bedrijfsmiddelen),
    listBedreigingen: () => get(TENANT_PATHS.bedreigingen),
    listBestandssystemen: () => get(TENANT_PATHS.bestandssystemen),
    listBewijscontainers: () => get(TENANT_PATHS.bewijscontainers),
    listTickets: () => get(TENANT_PATHS.tickets),
    listImplementationPrograms: () => get(TENANT_PATHS.implementationPrograms),
    listPdcaSchedules: () => get(TENANT_PATHS.pdcaSchedules),
    listVerbeterpunten: () => get(TENANT_PATHS.verbeterpunten),
    listPartnerRoles: () => get(TENANT_PATHS.partnerRoles),
    listExternalParties: () => get(TENANT_PATHS.externalParties),
    createExternalParty: (i) => post(TENANT_PATHS.externalParties, i),
    listProcessorAgreements: () => get(TENANT_PATHS.processorAgreements),
    listProcessingSchedules: () => get(TENANT_PATHS.processingSchedules),
    listSubProcessors: () => get(TENANT_PATHS.subProcessors),

    createVerwerking: (i) => post(TENANT_PATHS.verwerkingen, i),
    createActivity: (i) => post(TENANT_PATHS.activities, i),
    createBedrijfsmiddel: (i) => post(TENANT_PATHS.bedrijfsmiddelen, i),
    createBedreiging: (i) => post(TENANT_PATHS.bedreigingen, i),
    createBestandssysteem: (i) => post(TENANT_PATHS.bestandssystemen, i),
    createBewijscontainer: (i) => post(TENANT_PATHS.bewijscontainers, i),
    createTicket: (i) => post(TENANT_PATHS.tickets, i),
    createImplementationProgram: (i) => post(TENANT_PATHS.implementationPrograms, i),
    createPdcaSchedule: (i) => post(TENANT_PATHS.pdcaSchedules, i),
    createVerbeterpunt: (i) => post(TENANT_PATHS.verbeterpunten, i),
    createPartnerRole: (i) => post(TENANT_PATHS.partnerRoles, i),
    createProcessorAgreement: (i) => post(TENANT_PATHS.processorAgreements, i),
    createProcessingSchedule: (i) => post(TENANT_PATHS.processingSchedules, i),
    createSubProcessor: (i) => post(TENANT_PATHS.subProcessors, i),

    listSharedEngagements: () => get(TENANT_PATHS.sharedEngagements),
    createSharedEngagement: (i) => post(TENANT_PATHS.sharedEngagements, i),
    confirmSharedEngagement: (engagementId) => post(TENANT_PATHS.sharedEngagementConfirm, { engagementId }),
    listSharedDpas: () => get(TENANT_PATHS.sharedDpas),
    createSharedDpa: (i) => post(TENANT_PATHS.sharedDpas, i),
    listProcessorInvitations: () => get(TENANT_PATHS.sharedInvitations),
    createProcessorInvitation: (i) => post(TENANT_PATHS.sharedInvitations, i),

    listCatalog: (library) => request(cfg, admin(), `/admin/${library}`),
    getCatalogRow: (library, id) => request(cfg, admin(), `/admin/${library}/${id}`),
    createCatalogRow: (library, body) => request(cfg, admin(), `/admin/${library}`, { method: "POST", body: JSON.stringify(body) }),
    updateCatalogRow: (library, id, patch) => request(cfg, admin(), `/admin/${library}`, { method: "PATCH", body: JSON.stringify({ id, ...patch }) }),
    softDeleteCatalogRow: (library, id) => request(cfg, admin(), `/admin/${library}`, { method: "DELETE", body: JSON.stringify({ id }) }),
    restoreCatalogRow: (library, id) => request(cfg, admin(), `/admin/${library}`, { method: "PATCH", body: JSON.stringify({ id, isActive: true, deletedAt: null }) }),
    getConsultancyFirmDetail: (firmId) => request(cfg, admin(), `/admin/consultancy-firms/${encodeURIComponent(firmId)}`),
    addFirmCertification: (firmId, input) => request(cfg, admin(), `/admin/consultancy-firms/${encodeURIComponent(firmId)}/certifications`, { method: "POST", body: JSON.stringify(input) }),
    deleteFirmCertification: (firmId, certificationId) => request(cfg, admin(), `/admin/consultancy-firms/${encodeURIComponent(firmId)}/certifications`, { method: "DELETE", body: JSON.stringify({ certificationId }) }),
    listSubscriptions: () => request(cfg, admin(), `/admin/billing/subscriptions`),
    listCounselReviews: () => request(cfg, admin(), `/admin/counsel-reviews`),
    getCounselReviewDetail: (id) => request(cfg, admin(), `/admin/counsel-reviews/${encodeURIComponent(id)}`),
    submitCounselVerdict: (id, verdict, notes) => request(cfg, admin(), `/admin/counsel-reviews`, { method: "PATCH", body: JSON.stringify({ id, verdict, notes }) }),

    // D8 hardening (W21): checkout now goes through the authenticated tenant-worker, which uses the
    // SESSION's tenant — the client-supplied tenantId is ignored server-side. The old direct
    // billing-worker call (client-trusted tenantId) is retired from the client path.
    createCheckoutSession: (input) =>
      post("/billing/checkout", { ...(input.returnUrl ? { returnUrl: input.returnUrl } : {}) }),

    // C6.1 Trust Exchange
    listVisibleMeasures: () => get("/trust/measures"),
    publishMeasure: (i) => post("/trust/measures", i),
    consumeMeasure: (i) => post("/trust/measures/consume", i),
    listConsumedMeasures: () => get("/trust/consumed"),
    listMeasureMappings: (measureId) => get(`/trust/mappings?measureId=${encodeURIComponent(measureId)}`),
    mapMeasureToArticle: (i) => post("/trust/mappings", i),
    listMeasuresByArticle: (regulationArticleId) => get(`/trust/measures-by-article?regulationArticleId=${encodeURIComponent(regulationArticleId)}`),
    attachMeasureEvidence: (i) => post("/trust/evidence", i),
    listMeasureEvidence: (measureId) => get(`/trust/evidence?measureId=${encodeURIComponent(measureId)}`),

    // C4.5 compliance rollup + C3.1 eval runs (admin)
    getComplianceRollup: (tenantId) => request(cfg, admin(), `/admin/compliance-rollup?tenantId=${encodeURIComponent(tenantId)}`),
    refreshComplianceRollup: () => request(cfg, admin(), `/admin/compliance-rollup/refresh`, { method: "POST", body: "{}" }),
    listEvalRuns: (evalSetId) => request(cfg, admin(), `/admin/eval-runs?evalSetId=${encodeURIComponent(evalSetId)}`),

    // C6.2 Consultant marketplace
    listApprovedConsultants: () => get("/consultants"),
    listConsultantEngagements: () => get("/consultant-engagements"),
    createConsultantEngagement: (i) => post("/consultant-engagements", i),
    signConsultantEngagement: (engagementId, side) => post("/consultant-engagements/sign", { engagementId, side }),

    mcp: async (method, params) =>
      request(cfg, mcpBase(), "/", { method: "POST", body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }) }),
  };
}
